import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Brain, Eye, EyeOff } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardBody } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { useToast } from '@/hooks/useToast';
import { getErrorMessage } from '@/services/apiClient';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import {
  getAiSettings,
  updateAiSettings,
} from '@/services/aiSettings.service';

const AWS_REGIONS = [
  { value: '', label: 'Default (us-east-1)' },
  { value: 'us-east-1', label: 'US East (N. Virginia)' },
  { value: 'us-east-2', label: 'US East (Ohio)' },
  { value: 'us-west-1', label: 'US West (N. California)' },
  { value: 'us-west-2', label: 'US West (Oregon)' },
  { value: 'eu-west-1', label: 'EU (Ireland)' },
  { value: 'eu-central-1', label: 'EU (Frankfurt)' },
  { value: 'ap-northeast-1', label: 'Asia Pacific (Tokyo)' },
  { value: 'ap-southeast-1', label: 'Asia Pacific (Singapore)' },
  { value: 'ap-southeast-2', label: 'Asia Pacific (Sydney)' },
];

/**
 * Locked (plan 2026-10-10-001 U7: R8, R18): the stored credentials show,
 * still masked, with no Add/Update Credentials and no editing hints; nothing
 * says why.
 */
export function AiSettingsAdmin() {
  const locked = useAdminPageLevel() === 'locked';
  const toast = useToast();
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'ai-settings'],
    queryFn: getAiSettings,
    staleTime: 30_000,
  });

  const [accessKeyId, setAccessKeyId] = useState('');
  const [secretAccessKey, setSecretAccessKey] = useState('');
  const [region, setRegion] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [editing, setEditing] = useState(false);

  const saveMutation = useMutation({
    mutationFn: updateAiSettings,
    onSuccess: (res) => {
      toast.success('AI credentials saved.');
      qc.setQueryData(['admin', 'ai-settings'], res);
      setEditing(false);
      setAccessKeyId('');
      setSecretAccessKey('');
      setRegion('');
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Failed to save credentials.')),
  });

  function handleSave() {
    if (locked) return;
    const payload: Record<string, string> = {};
    if (accessKeyId) payload.access_key_id = accessKeyId;
    if (secretAccessKey) payload.secret_access_key = secretAccessKey;
    if (region) payload.region = region;

    if (!Object.keys(payload).length) {
      toast.error('Please fill in at least one field.');
      return;
    }
    saveMutation.mutate(payload);
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-2xl">
      <PageHeader title="AI Settings" />

      <Card>
          <CardBody>
            <div className="flex items-center gap-3 mb-4">
              <Brain className="h-5 w-5 text-violet-500" />
              <h3 className="text-base font-semibold text-slate-200">AWS Bedrock Credentials</h3>
              {data?.configured ? (
                <Badge variant="success">Configured</Badge>
              ) : (
                <Badge variant="neutral">Not set up</Badge>
              )}
            </div>

            <p className="text-sm text-slate-400 mb-5">
              Provide your own AWS access keys for Bedrock AI services. When set, all AI features
              for this account will use these credentials instead of the shared platform keys.
              {!locked && !data?.configured && ' Dictation and AI writing in patient notes stay off until both keys are saved.'}
              {!locked && data?.configured && ' Once configured, credentials can be updated but not removed.'}
            </p>

            {(locked || !editing) && data?.configured && (
              <div className="space-y-3 mb-5">
                <div>
                  <span className="text-xs font-medium text-slate-400 uppercase tracking-wide">Access Key ID</span>
                  <p className="text-sm text-slate-200 font-mono mt-0.5">{data.credentials.access_key_id}</p>
                </div>
                <div>
                  <span className="text-xs font-medium text-slate-400 uppercase tracking-wide">Secret Access Key</span>
                  <p className="text-sm text-slate-200 font-mono mt-0.5">{data.credentials.secret_access_key}</p>
                </div>
                {data.credentials.region && (
                  <div>
                    <span className="text-xs font-medium text-slate-400 uppercase tracking-wide">Region</span>
                    <p className="text-sm text-slate-200 mt-0.5">{data.credentials.region}</p>
                  </div>
                )}
              </div>
            )}

            {/* Locked: no Add/Update, so the form never opens. */}
            {locked ? null : editing ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">
                    Access Key ID
                  </label>
                  <Input
                    value={accessKeyId}
                    onChange={(e) => setAccessKeyId(e.target.value)}
                    placeholder="AKIA..."
                    className="font-mono"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">
                    Secret Access Key
                  </label>
                  <div className="relative">
                    <Input
                      type={showSecret ? 'text' : 'password'}
                      value={secretAccessKey}
                      onChange={(e) => setSecretAccessKey(e.target.value)}
                      placeholder={data?.configured ? 'Leave blank to keep current' : 'Enter secret key'}
                      className="font-mono pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowSecret(!showSecret)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200"
                    >
                      {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">
                    Region
                  </label>
                  <Select
                    value={region || null}
                    onChange={(val) => setRegion(val ?? '')}
                    options={AWS_REGIONS}
                    placeholder="Default (us-east-1)"
                    clearable
                  />
                </div>

                <div className="flex gap-3 pt-2">
                  <Button
                    onClick={handleSave}
                    disabled={saveMutation.isPending}
                  >
                    {saveMutation.isPending ? 'Saving...' : 'Save Credentials'}
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setEditing(false);
                      setAccessKeyId('');
                      setSecretAccessKey('');
                      setRegion('');
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex gap-3">
                <Button onClick={() => setEditing(true)}>
                  {data?.configured ? 'Update Credentials' : 'Add Credentials'}
                </Button>
              </div>
            )}
          </CardBody>
        </Card>
      </div>
  );
}
